import { atom } from 'recoil';

import { SelectableArray } from '@type-def_common/SelectableArray';

import { LandCurrentStatus, ResidenceCurrentStatus } from '@constants_iqfu';

export const lastSearchedPropertyTypeAtom = atom<keyof typeof IqfuProSearchablePropertyQuery | undefined>({
  key: 'iqfuProLastSearchedPropertyType',
  default: undefined,
});

export type lastSearchedTownQuery =
  | {
      prefecture: string;
      city: string;
      town: string;
      addressSummary: string;
    }
  | undefined;

export const lastSearchedTownQueryAtom = atom<lastSearchedTownQuery>({
  key: 'iqfuProLastSearchedTownQuery',
  default: undefined,
});

export const lastSearcheMansionIdAtom = atom<number>({
  key: 'iqfuProLastSearchedMansionId',
  default: 0,
});

export const lastSearcheMansionDisplayNameAtom = atom<string>({
  key: 'iqfuProLastSearcheMansionDisplayName',
  default: '',
});

export const currentMansionDisplayNameAtom = atom<string>({
  key: 'iqfuProCurrentMansionDisplayName',
  default: '',
});

export const IqfuProBuildingStructures = {
  wooden: '木造',
  light_steel: '軽量鉄骨造',
  steel: '(重量)鉄骨造',
  rc: '鉄筋コンクリート',
  src: '鉄骨鉄筋コンクリート',
  other_building_structures: 'その他',
};

export const IqfuProSearchablePropertyQuery = {
  landhouse: '土地・戸建て',
  mansion: 'マンション',
};

export const IqfuProResultPropertyType = {
  mansion: 'マンション',
  land: '土地',
  new_house: '新築戸建て',
  used_house: '中古戸建て',
};

export type SellingContract = {
  id: number;
  price: number;
  building_price: null | number;
  land_price: null | number;
  property_type: keyof typeof IqfuProResultPropertyType;
  address_summary: string;
  mansion_name: string;
  mansion_full_address: string;
  contract_date: string;
  land_area: number | null;
  building_area: number | null;
  occupied_area: number | null;
  has_reformed: boolean;
  building_structure: keyof typeof IqfuProBuildingStructures | null;
  built_date: string | null;
  floor: number | null;
  floor_plan: string | null;
  has_information_map: boolean;
  current_status: keyof typeof LandCurrentStatus | keyof typeof ResidenceCurrentStatus;
  // PROの検索時のみ返ってくるレスポンスなのでoptional演算子に
  detail_availability?: boolean;
};

export const triggeredSearchFlagAtom = atom<boolean>({
  key: 'triggeredSearchFlagAtom',
  default: false,
});

export const searchResultDataAtom = atom<SellingContract[]>({
  key: 'iqfuProSearchResultData',
  default: [],
});

export const neighboringSellingContractDataAtom = atom<SellingContract[]>({
  key: 'iqfuProNeighboringSellingContractDataAtom',
  default: [],
});

//neighboringSellingContractDataAtomを分割して表示する、表示用のdata
export const displayNeighboringSellingContractDataAtom = atom<SellingContract[][]>({
  key: 'iqfuProDisplayNeighboringSellingContractDataAtom',
  default: [],
});

export const lastSearchOffsetNumberAtom = atom<number>({
  key: 'iqfuProLastSearchOffsetNumber',
  default: 0,
});

export const lastSearchedPageTotalCountAtom = atom<number>({
  key: 'iqfuProLastSearchedPageTotalCount',
  default: 0,
});

export const lastSearchedPageNumberAtom = atom<number>({
  key: 'iqfuProLastSearchedPageNumber',
  default: 1,
});

export const neighboringContractsPageTotalCountAtom = atom<number>({
  key: 'iqfuProNeighboringContractsPageTotalCount',
  default: 0,
});

export const neighboringContractsPageNumberAtom = atom<number>({
  key: 'iqfuProNeighboringContractsPageNumber',
  default: 1,
});

export const propertyTypeAtom = atom<keyof typeof IqfuProSearchablePropertyQuery>({
  key: 'iqfuProPropertyTypeAtom',
  default: 'landhouse',
});

export const prefectureAtom = atom<string>({
  key: 'iqfuProPrefectureAtom',
  default: '',
});

export const displayPrefectureNameAtom = atom<string>({
  key: 'iqfuProDisplayPrefectureNameAtom',
  default: '',
});

export const cityAtom = atom<string>({
  key: 'iqfuProCityAtom',
  default: '',
});

export const displayCityNameAtom = atom<string>({
  key: 'iqfuProDisplayCityNameAtom',
  default: '',
});

export const townAtom = atom<string>({
  key: 'iqfuProTownAtom',
  default: '',
});

export const displayTownNameAtom = atom<string>({
  key: 'iqfuProDisplayTownNameAtom',
  default: '',
});

export const hasInitializedAtom = atom<boolean>({
  key: 'iqfuProHasInitialized',
  default: false,
});

export const selectableCitiesAtom = atom<SelectableArray>({
  key: 'iqfuProSelectableCitiesAtom',
  default: {},
});

export const selectableTownsAtom = atom<SelectableArray>({
  key: 'iqfuProSelectableTowns',
  default: {},
});

//一回のfetchで取得する件数
export const fetchSaleContractsLimitNumber = 20;
